import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { Merchant, PaymentCard, PaymentCardTypeLabel, StripeService } from '@galaxy/billing';
import { EditContactInfoDialogComponent, EditPaymentMethodDialogComponent } from '@vendasta/billing-ui';
import { Observable, Subscription } from 'rxjs';
import { map, take, withLatestFrom } from 'rxjs/operators';
import { BillingSettingsService } from '../shared-billing/settings/billing-settings.service';
import { BillingAction, CheckoutService, PaymentInfo } from './checkout.service';

@Component({
  selector: 'app-instant-bill-payment-info',
  template: `
    <ng-container *ngIf="billingAction$ | async as status">
      <p *ngIf="status === BillingAction.NO_MERCHANT_CONFIGURED">
        <span>{{ 'CHECKOUT.CONTACT_NOT_CONFIGURED' | translate }}</span>
        <span *ngIf="editable" class="span-text" (click)="openEditContactDialog()">
          <a>{{ 'CHECKOUT.ADD' | translate }}</a>
        </span>
      </p>
      <p *ngIf="status === BillingAction.NO_PAYMENT_CONFIGURED">
        <span>{{ 'CHECKOUT.PAYMENT_NOT_CONFIGURED' | translate }}</span>
        <span *ngIf="editable" class="span-text" (click)="openEditPaymentDialog()">
          <a>{{ 'CHECKOUT.ADD' | translate }}</a>
        </span>
      </p>
      <p *ngIf="status === BillingAction.INVALID_PAYMENT">
        <span>{{ 'CHECKOUT.INVALID_PAYMENT' | translate }}</span>
        <span *ngIf="editable" class="span-text" (click)="openEditPaymentDialog()">
          <a>{{ 'CHECKOUT.EDIT' | translate }}</a>
        </span>
      </p>
      <p *ngIf="status === BillingAction.PAYMENT_CONFIGURED && paymentInfo$ | async as info">
        <mat-checkbox #consent (change)="checkboxClicked($event)" color="primary">
          <ng-container *ngIf="paymentRequired">{{ 'CHECKOUT.CHARGE' | translate }}</ng-container>
          <ng-container *ngIf="!paymentRequired">{{ 'CHECKOUT.CHARGE_IF_APPLICABLE' | translate }}</ng-container>
        </mat-checkbox>
        <span class="span-text">
          <i [ngClass]="info?.paymentClass" class="pf" id="brand-icon"></i>
          {{ 'CHECKOUT.ENDING_IN' | translate: { lastFourDigits: info?.lastFourDigits } }}
        </span>
        <span *ngIf="editable" class="span-text" (click)="openEditPaymentDialog()">
          <a>{{ 'CHECKOUT.EDIT' | translate }}</a>
        </span>
      </p>
    </ng-container>
  `,
  styleUrls: ['./instant-bill-payment-info.component.scss'],
})
export class InstantBillPaymentInfoComponent implements OnInit, OnDestroy {
  @ViewChild('consent') consentCheckbox: MatCheckbox;
  @Input() editable = false;
  @Input() merchantId: string;
  @Input() productSKU: string;
  @Input() paymentRequired: boolean;

  billingAction$: Observable<BillingAction>;
  paymentInfo$: Observable<PaymentInfo>;
  BillingAction = BillingAction;

  stripeKey$: Observable<string>;

  subscriptions: Subscription[] = [];

  constructor(
    private snapshotCheckoutService: CheckoutService,
    private dialog: MatDialog,
    private billingSettingsService: BillingSettingsService,
    private stripeService: StripeService,
  ) {}

  ngOnInit(): void {
    this.snapshotCheckoutService.loadProductRequest(this.merchantId, this.productSKU);

    this.billingAction$ = this.snapshotCheckoutService.billingAction$;
    this.paymentInfo$ = this.billingSettingsService.paymentDetails.pipe(
      map((paymentDetails: PaymentCard) => {
        let paymentClass = '';
        if (paymentDetails.cardId.length) {
          paymentClass = this.getPaymentFontClass(paymentDetails.cardType);
        }
        return {
          lastFourDigits: paymentDetails.lastFourDigits,
          paymentClass: paymentClass,
        };
      }),
    );

    this.stripeKey$ = this.stripeService.getPublicKeyByMerchant(this.merchantId);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  openEditContactDialog(): void {
    this.subscriptions.push(
      this.billingSettingsService.merchantDetailsInfo.pipe(take(1)).subscribe((contactInfo) => {
        const dialogRef = this.dialog.open(EditContactInfoDialogComponent, {
          width: '500px',
          data: {
            merchantId: this.merchantId,
            merchant: contactInfo.merchantId ? (contactInfo as Merchant) : null,
          },
        });
        dialogRef
          .afterClosed()
          .pipe(take(1))
          .subscribe((_) => {
            this.snapshotCheckoutService.loadProductRequest(this.merchantId, this.productSKU);
          });
      }),
    );
  }

  openEditPaymentDialog(): void {
    if (this.consentCheckbox) {
      this.consentCheckbox.checked = false;
      this.snapshotCheckoutService.instantBillConsentChange(false);
    }

    this.subscriptions.push(
      this.stripeKey$
        .pipe(
          withLatestFrom(this.billingSettingsService.paymentDetailsInfo),
          map(([stripeKey, card]) => {
            const dialogRef = this.dialog.open(EditPaymentMethodDialogComponent, {
              width: '500px',
              data: {
                merchantId: this.merchantId,
                stripePublicKey: stripeKey,
                recaptchaKey: '6Lf0gKwUAAAAAMiDLNbVfgyFN8fzT3KDSkWeC92d',
                requireRecaptcha: true,
                paymentCard: card !== this.billingSettingsService.emptyPaymentDetails ? card : null,
              },
            });
            dialogRef
              .afterClosed()
              .pipe(take(1))
              .subscribe((merchantUpdated) => {
                if (merchantUpdated) {
                  this.billingSettingsService.loadMerchantCreditCards();
                }
                this.snapshotCheckoutService.loadProductRequest(this.merchantId, this.productSKU);
              });
          }),
          take(1),
        )
        .subscribe(),
    );
  }

  checkboxClicked(change: MatCheckboxChange): void {
    this.snapshotCheckoutService.instantBillConsentChange(change.checked);
  }

  getPaymentFontClass(cardType: PaymentCardTypeLabel): string {
    const cardBrandToPfClass = {
      Visa: 'pf-visa',
      Mastercard: 'pf-mastercard',
      'American Express': 'pf-american-express',
      Discover: 'pf-discover',
      'Diners Club': 'pf-diners',
      JCB: 'pf-jcb',
    };
    return Object.prototype.hasOwnProperty.call(cardBrandToPfClass, cardType.value)
      ? cardBrandToPfClass[cardType.value]
      : 'pf-credit-card';
  }
}
